import { format } from 'date-fns'
import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useSearchParams } from 'react-router-dom'

import Spinner from '@/compoments/Spinner'
import { getUser } from '@/features/auth/authSlice'
import {
    selectActiveChat,
    selectMessages,
    selectPagination,
    setEditMessage,
} from '@/features/chat/chatSlice'
import {
    useGetChatByIdQuery,
    useMarcUsReadMutation,
    useRemoveMessageMutation,
} from '@/services/chats/chats'
import type { User } from '@/types'
import type { IChat, IMessage } from '@/types/chats'

import { ChatFooter } from './ChatFooter'
import { ChatHeader } from './ChatHeader'
import { Message } from './Message'

interface ChatMessagesProps {
    searchAction: React.Dispatch<React.SetStateAction<boolean>>
}

export const ChatMessages = ({ searchAction }: ChatMessagesProps) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const messageId = searchParams.get('messageId')
    const { id } = useParams()
    const dispatch = useDispatch()
    const [currentPage, setCurrentPage] = useState(1)
    const [markMessagesAsRead] = useMarcUsReadMutation()
    const activeChat = useSelector(selectActiveChat)
    const messages = useSelector(selectMessages)
    const pagination = useSelector(selectPagination)
    const hasMore = currentPage < (pagination?.maxPages || 0)
    const user = useSelector(getUser)
    const [isFirstScroll, setIsFirstScroll] = useState(false)
    const lastScrollTop = useRef(0)
    // const [groupedMessages, setGroupedMessages] = useState<
    //     Record<string, IMessage[]>
    // >({})

    const { isLoading, refetch } = useGetChatByIdQuery(
        {
            id: id || '',
            query: `sort=createdAt&order=desc&page=${currentPage}&limit=10${messageId ? `&messageId=${messageId}` : ''}`,
        },
        { skip: !id },
    )

    useEffect(() => {
        if (id && currentPage) {
            refetch()
        }
    }, [id, refetch, dispatch, currentPage])

    useEffect(() => {
        setCurrentPage(1)
        setIsFirstScroll(false)
    }, [id])

    const fetchMoreMessages = () => {
        if (hasMore) {
            const element = elementRef?.current
            if (element) {
                if (
                    element.scrollHeight +
                        element.scrollTop -
                        element.clientHeight ===
                    0
                ) {
                    lastScrollTop.current = element.scrollTop
                }
            }
            setCurrentPage(prevPage => prevPage + 1)
        }
    }
    const elementRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!elementRef.current) return
        if (currentPage === 1 && !isFirstScroll) {
            elementRef.current.scrollTo({
                top: elementRef.current.scrollHeight,
                // behavior: 'smooth',
            })
            setIsFirstScroll(true)
            return
        } else if (lastScrollTop.current < 0) {
            elementRef.current.scrollTop = lastScrollTop.current
            lastScrollTop.current = 0
            return
        }
    }, [messages])

    const groupMessagesByDay = (messages: IMessage[], id: number) => {
        const unreadMessages: IMessage[] = []
        const groupedMessages = messages?.reduce(
            (acc, message) => {
                const dateKey = message?.createdAt
                    ? format(new Date(message?.createdAt), 'yyyy-MM-dd')
                    : ''

                const updatedMessage =
                    +message?.senderId === +id
                        ? { ...message, isRead: true }
                        : message

                if (!updatedMessage.isRead) {
                    unreadMessages.push(updatedMessage)
                    return acc
                }

                if (!acc[dateKey]) {
                    acc[dateKey] = []
                }
                acc[dateKey].push(updatedMessage)
                return acc
            },
            {} as Record<string, IMessage[]>,
        )
        return { ...groupedMessages, unreadMessages }
    }

    const groupedMessages: Record<string, IMessage[]> = useMemo(() => {
        if (!messages || !user?.id) return {}
        return groupMessagesByDay(messages, user.id)
    }, [messages, user?.id])

    // useEffect(() => {
    //     if (messages && user?.id) {
    //         const groupedMessages = groupMessagesByDay(messages, user?.id)
    //         setGroupedMessages(groupedMessages)
    //     }
    // }, [messages, user])

    const messagesRefs = useRef<Record<string, HTMLElement>>({})
    const previousVisibleUnreadIds = useRef<string[]>([])

    useLayoutEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                const visibleUnreadIds = entries
                    .filter(entry => entry.isIntersecting)
                    .map(
                        entry => entry.target.getAttribute('data-id') as string,
                    )
                    .filter(Boolean)
                    .filter(
                        id => !previousVisibleUnreadIds.current.includes(id),
                    )

                if (visibleUnreadIds.length > 0) {
                    markMessagesAsRead({
                        messageIds: visibleUnreadIds.map(o => +o),
                    })
                    previousVisibleUnreadIds.current = [
                        ...previousVisibleUnreadIds.current,
                        ...visibleUnreadIds,
                    ]
                }
            },
            {
                root: elementRef.current,
                threshold: 0.5,
            },
        )
        Object.values(messagesRefs.current).forEach(el => {
            if (el) {
                observer.observe(el)
            }
        })

        return () => {
            observer.disconnect()
            messagesRefs.current = {}
            previousVisibleUnreadIds.current = []
        }
    }, [id, groupedMessages])

    useEffect(() => {
        if (!messageId) return
        if (messageId) {
            setTimeout(() => {
                const targetElement = messagesRefs?.current?.[+messageId]
                if (targetElement) {
                    targetElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                    setTimeout(() => {
                        setSearchParams(prev => {
                            prev.delete('messageId')
                            return prev
                        })
                    }, 500)
                }
            }, 100)
        }
    }, [messages, messageId, messagesRefs])

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center flex-auto">
                <div className="flex items-center justify-center min-h-300">
                    <Spinner />
                </div>
            </div>
        )
    }

    return activeChat ? (
        <>
            <div className="chat-workflow">
                <ChatHeader
                    chat={activeChat}
                    searchAction={searchAction}
                />
                {messages.length ? (
                    <div
                        id="scrollableDiv"
                        ref={elementRef}
                        className="chat-workflow--body"
                        style={{
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column-reverse',
                        }}
                    >
                        <InfiniteScroll
                            dataLength={messages.length}
                            next={fetchMoreMessages}
                            hasMore={hasMore}
                            style={{
                                display: 'flex',
                                flexDirection: 'column-reverse',
                            }}
                            inverse={true}
                            scrollableTarget="scrollableDiv"
                            scrollThreshold={'0px'}
                            initialScrollY={0}
                            loader={
                                <div className="flex items-center justify-center">
                                    <Spinner size="xs" />
                                </div>
                            }
                        >
                            <div className="chat-message-list">
                                {Object.entries(groupedMessages).map(
                                    ([group, messages], index) => {
                                        return (
                                            <MessagesGroup
                                                key={index}
                                                group={group}
                                                messages={messages}
                                                messagesRefs={messagesRefs}
                                                chat={activeChat}
                                            />
                                        )
                                    },
                                )}
                            </div>
                        </InfiniteScroll>
                    </div>
                ) : (
                    <div className="flex flex-col justify-center items-center flex-auto">
                        <p className="color-text-color-1">
                            There is no messages yet
                        </p>
                    </div>
                )}
                <ChatFooter chat={activeChat} />
            </div>
        </>
    ) : (
        <div className="flex flex-col justify-center items-center flex-auto">
            <p className="color-text-color-1">Select chat</p>
        </div>
    )
}

interface MessagesGroupProps {
    group: string
    messages: IMessage[]
    messagesRefs: React.MutableRefObject<Record<string, HTMLElement>>
    chat: IChat
}

function MessagesGroup({
    group,
    messages,
    messagesRefs,
    chat,
}: MessagesGroupProps) {
    const [searchParams] = useSearchParams()
    const messageId = searchParams.get('messageId')
    const dispatch = useDispatch()
    const [deleteMessage] = useRemoveMessageMutation()

    if (!messages?.length) return null

    const handleUnreadMessageRef = (msg: IMessage, el: HTMLElement | null) => {
        const { isRead, id } = msg
        if (messageId && el && +messageId === id) {
            messagesRefs.current[id] = el
            return
        }
        if (isRead) return null
        if (el) {
            messagesRefs.current[id] = el
        }
    }

    const getUser = (senderId: number): User | undefined => {
        return chat.users.find((user: User) => user.id === senderId)
    }

    return (
        <div>
            <div className="date-separator">
                <span className="date-text">
                    {group === 'unreadMessages' ? 'Unread Messages' : group}
                </span>
            </div>
            {messages.map((msg: IMessage) => (
                <div
                    key={msg.id}
                    data-id={msg.isSystem ? undefined : msg.id}
                    className={
                        messageId === msg.id.toString() ? 'search-message' : ''
                    }
                    ref={el => handleUnreadMessageRef(msg, el)}
                >
                    <Message
                        message={msg}
                        deleteMessage={deleteMessage}
                        editMessage={msg => dispatch(setEditMessage(msg))}
                    />
                </div>
            ))}
        </div>
    )
}
